/**
 * ردیف‌های اصلاحیِ حقوق — داخلِ «ویرایش رکورد حقوق» (نفر‌به‌نفر).
 *
 * خواستهٔ کاربر: «شاید یه سری موارد جزئی جا بمونه یا یه سری مبالغ یه‌هو عوض
 * بشه؛ بتونیم ردیف اضافه کنیم — عملیاتِ کارمزدی، روزمزد، یا مبلغ — و همه‌شون
 * قابلِ ویرایش و اصلاح باشن. فقط واسه محاسبهٔ حقوقِ همون ماه.»
 *
 * این جدول به تولید/حضور/دفترِ مالی دست نمی‌زند؛ ردیف‌ها فقط برای همین
 * (پرسنل، دوره) ذخیره می‌شوند و موتورِ حقوق آن‌ها را می‌خواند. ذخیرهٔ واقعی
 * دستِ والد است (دکمهٔ «ذخیره»ی مودال) — اینجا فقط پیش‌نویسِ ردیف‌ها ویرایش می‌شود.
 */
import React, { useMemo } from "react";
import { AutoComplete, Button, Input, InputNumber, Select, Space, Table, Tooltip, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { MoneyInput, formatMoney } from "../utils/money";

const { Text } = Typography;

export const ADJ_KINDS = [
  { value: "piece", label: "عملیات کارمزدی", unit: "عدد" },
  { value: "daily", label: "روزمزد", unit: "روز" },
  { value: "hourly", label: "ساعتی", unit: "ساعت" },
  { value: "amount", label: "مبلغ", unit: null },
];
const KIND = Object.fromEntries(ADJ_KINDS.map((k) => [k.value, k]));
const DIRECTIONS = [
  { value: "earn", label: "افزایش" },
  { value: "deduct", label: "کسر" },
];

let seq = 0;
/** ردیفِ تازهٔ پیش‌نویس (کلیدِ محلی برای React؛ id بعد از ذخیره از سرور می‌آید). */
export function newAdjRow(patch = {}) {
  seq += 1;
  return {
    _key: `n${seq}`, id: null, direction: "earn", kind: "amount", title: "",
    catalog_id: null, quantity: null, unit_price: null, amount: null, note: "", ...patch,
  };
}

/** مبلغِ یک ردیف — همان قاعدهٔ سرور: تعداد×فی، یا مبلغِ مستقیم. */
export function rowAmount(r) {
  if (!r) return 0;
  if (r.kind === "amount") return Number(r.amount || 0);
  // ساعتی از دقیقهٔ دقیق — همان قاعدهٔ سرور (v2.112.6)
  if (r.kind === "hourly") return Math.round(Number(r.unit_price || 0) * Math.round(Number(r.quantity || 0) * 60) / 60);
  return Math.round(Number(r.quantity || 0) * Number(r.unit_price || 0));
}

/** ساعتِ اعشاری ⇄ (ساعت، دقیقه) */
const splitHours = (q) => {
  const m = Math.round(Number(q || 0) * 60);
  return { h: Math.floor(m / 60), m: m % 60 };
};
const joinHours = (h, m) => {
  const total = (Number(h) || 0) * 60 + (Number(m) || 0);
  return total > 0 ? total / 60 : null;
};

export function adjTotals(rows) {
  return (rows || []).reduce((a, r) => {
    const v = rowAmount(r);
    if (r.direction === "deduct") a.deduct += v; else a.earn += v;
    return a;
  }, { earn: 0, deduct: 0 });
}

/** خروجیِ سرور → پیش‌نویسِ قابلِ ویرایش. */
export function toDraftRows(items) {
  return (items || []).map((it) => ({
    _key: `s${it.id}`, id: it.id, direction: it.direction || "earn", kind: it.kind || "amount",
    title: it.title || "", catalog_id: it.catalog_id ?? null,
    quantity: it.quantity ?? null, unit_price: it.unit_price ?? null,
    amount: it.amount ?? null, note: it.note || "",
  }));
}

/** پیش‌نویس → بدنهٔ PUT (فقط فیلدهای معنادارِ هر نوع). */
export function toApiItems(rows) {
  return (rows || []).map((r) => ({
    id: r.id || undefined, direction: r.direction, kind: r.kind, title: (r.title || "").trim(),
    catalog_id: r.catalog_id || undefined,
    quantity: r.kind === "amount" ? undefined : r.quantity,
    unit_price: r.kind === "amount" ? undefined : r.unit_price,
    amount: r.kind === "amount" ? r.amount : undefined,
    note: (r.note || "").trim() || undefined,
  }));
}

/** اعتبارسنجیِ سمتِ کاربر — پیامِ اولین خطا یا null (سرور هم دوباره چک می‌کند). */
export function validateRows(rows) {
  for (const r of rows || []) {
    const t = (r.title || "").trim();
    if (!t) return "شرحِ یکی از ردیف‌ها خالی است";
    if (r.kind !== "amount") {
      if (!(Number(r.quantity) > 0)) return `«${t}»: ${KIND[r.kind]?.unit || "تعداد"} باید بزرگ‌تر از صفر باشد`;
      if (r.unit_price == null || Number(r.unit_price) < 0) return `«${t}»: فی/نرخ لازم است`;
    }
    if (!(rowAmount(r) > 0)) return `«${t}»: مبلغِ ردیف صفر است`;
  }
  return null;
}

export default function PayrollAdjustmentsEditor({ rows = [], onChange, catalog = [], disabled, rateOptions }) {
  /* فیِ خودکار (v2.112.5): روزمزد از مدلِ حقوقِ همین ماه (بازهٔ روزمزد، حقوقِ ثابت ÷ ۳۰، روزمزدِ پایه) و ساعتی
     = روزانه ÷ مبنای ساعت (قاعدهٔ «هر ۷ ساعت = یک روز»). اولین گزینه پیش‌فرض است؛ کاربر می‌تواند عوض کند یا دستی بزند. */
  const optsFor = (kind) => (kind === "daily" ? (rateOptions?.daily || []).map((o) => ({ ...o, value: o.daily }))
    : kind === "hourly" ? (rateOptions?.hourly || []).map((o) => ({ ...o, value: o.hourly })) : []);
  const autoRate = (kind) => optsFor(kind)[0] || null;
  const set = (key, patch) => onChange(rows.map((r) => {
    if (r._key !== key) return r;
    const next = { ...r, ...patch };
    // تغییرِ نوع به روزمزد/ساعتی ⇒ فیِ همان نوع (اگر کاربر دستی چیزی نزده)
    if (patch.kind && patch.kind !== r.kind && (r.unit_price == null || r.rate_key)) {
      const o = autoRate(patch.kind);
      next.unit_price = o ? o.value : null;
      next.rate_key = o ? o.key : null;
    }
    return next;
  }));
  const remove = (key) => onChange(rows.filter((r) => r._key !== key));
  const add = (kind) => {
    const o = autoRate(kind);
    onChange([...rows, newAdjRow({ kind, ...(o ? { unit_price: o.value, rate_key: o.key } : {}) })]);
  };

  // نرخ‌نامه برای انتخابِ عملیات: نام + محصول → فیِ فعلی (snapshot در ردیف می‌نشیند)
  const opOptions = useMemo(() => (catalog || []).map((c) => ({
    value: `${c.operation_name} · ${c.product_type}`,
    label: `${c.operation_name} — ${c.product_type} — ${formatMoney(c.total_rate)}`,
    op: c,
  })), [catalog]);

  const totals = adjTotals(rows);

  const columns = [
    {
      title: "نوع", dataIndex: "kind", width: 150,
      render: (v, r) => (
        <Select size="small" value={v} style={{ width: "100%" }} disabled={disabled}
          options={ADJ_KINDS} onChange={(k) => set(r._key, { kind: k })} />
      ),
    },
    {
      title: "جهت", dataIndex: "direction", width: 100,
      render: (v, r) => (
        <Select size="small" value={v} style={{ width: "100%" }} disabled={disabled}
          options={DIRECTIONS} onChange={(d) => set(r._key, { direction: d })} />
      ),
    },
    {
      title: "شرح", dataIndex: "title", width: 210,
      render: (v, r) => (r.kind === "piece" ? (
        <AutoComplete size="small" value={v} style={{ width: "100%" }} disabled={disabled}
          options={opOptions} placeholder="نام عملیات (از نرخ‌نامه یا آزاد)"
          filterOption={(input, opt) => String(opt?.value || "").includes(input)}
          onChange={(t) => set(r._key, { title: t, catalog_id: null })}
          onSelect={(_, opt) => set(r._key, {
            title: opt.op.operation_name, catalog_id: opt.op.id,
            unit_price: r.unit_price == null ? opt.op.total_rate : r.unit_price,
          })} />
      ) : (
        <Input size="small" value={v} disabled={disabled} placeholder="شرحِ ردیف"
          onChange={(e) => set(r._key, { title: e.target.value })} />
      )),
    },
    {
      /* 🐞 v2.112.6: InputNumberِ باریک با پسوندِ «ساعت» و فلش‌ها عدد را می‌پوشاند (عدد بود ولی دیده نمی‌شد).
         ساعتی حالا «ساعت + دقیقه»؛ بقیه بدونِ فلش و پهن‌تر. */
      title: "تعداد", dataIndex: "quantity", width: 150,
      render: (v, r) => {
        if (r.kind === "amount") return <Text type="secondary">—</Text>;
        if (r.kind === "hourly") {
          const { h, m } = splitHours(v);
          return (
            <Space size={2} style={{ width: "100%" }}>
              <InputNumber size="small" controls={false} min={0} value={v == null ? null : h}
                style={{ width: 70 }} disabled={disabled} addonAfter="س"
                onChange={(x) => set(r._key, { quantity: joinHours(x, m) })} />
              <InputNumber size="small" controls={false} min={0} max={59} value={v == null ? null : m}
                style={{ width: 70 }} disabled={disabled} addonAfter="د"
                onChange={(x) => set(r._key, { quantity: joinHours(h, x) })} />
            </Space>
          );
        }
        return (
          <InputNumber size="small" controls={false} value={v} min={0} style={{ width: "100%" }} disabled={disabled}
            addonAfter={KIND[r.kind]?.unit}
            onChange={(q) => set(r._key, { quantity: q })} />
        );
      },
    },
    {
      title: "فی / نرخ", dataIndex: "unit_price", width: 200,
      render: (v, r) => {
        if (r.kind === "amount") return <Text type="secondary">—</Text>;
        const opts = optsFor(r.kind);
        return (
          <div>
            <MoneyInput size="small" value={v} disabled={disabled}
              onChange={(p) => set(r._key, { unit_price: p, rate_key: null })} />
            {opts.length > 0 && (
              <Select size="small" style={{ width: "100%", marginTop: 2 }} disabled={disabled}
                value={r.rate_key && opts.some((o) => o.key === r.rate_key) ? r.rate_key : undefined}
                placeholder="فی از…" popupMatchSelectWidth={false}
                options={opts.map((o) => ({ value: o.key, label: `${o.label} — ${formatMoney(o.value)}` }))}
                onChange={(k) => { const o = opts.find((x) => x.key === k); set(r._key, { unit_price: o.value, rate_key: k }); }} />
            )}
            {r.kind === "hourly" && rateOptions?.base_hours && (
              <Text type="secondary" style={{ fontSize: 10.5 }}>
                ساعتی = روزانه ÷ {rateOptions.base_hours} (هر {rateOptions.base_hours} ساعت = یک روز)
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "مبلغ (ریال)", dataIndex: "amount", width: 150, align: "left",
      render: (v, r) => (r.kind === "amount" ? (
        <MoneyInput size="small" value={v} disabled={disabled}
          onChange={(a) => set(r._key, { amount: a })} />
      ) : (
        <b style={{ color: r.direction === "deduct" ? "#C0392B" : "#107C41" }}>{formatMoney(rowAmount(r))}</b>
      )),
    },
    {
      title: "توضیح", dataIndex: "note", width: 140,
      render: (v, r) => (
        <Input size="small" value={v} disabled={disabled} placeholder="اختیاری"
          onChange={(e) => set(r._key, { note: e.target.value })} />
      ),
    },
    {
      title: "", key: "_del", width: 44, align: "center",
      render: (_, r) => (
        <Tooltip title="حذف ردیف">
          <Button size="small" danger type="text" icon={<DeleteOutlined />} disabled={disabled}
            onClick={() => remove(r._key)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="gx-adj-editor" style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <div>
          <Text strong style={{ fontSize: 12.5 }}>ردیف‌های اصلاحی (فقط برای محاسبهٔ همین ماه)</Text>
          <div style={{ fontSize: 11.5, color: "var(--text-muted,#6b7280)" }}>
            عملیاتِ جامانده، روزِ روزمزد یا مبلغِ اصلاحی را اینجا اضافه کنید؛ به تولید و حضور دست نمی‌زند و با «محاسبهٔ مجدد» هم نمی‌پرد.
          </div>
        </div>
        <Space size={4} wrap>
          <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={() => add("piece")}>عملیات کارمزدی</Button>
          <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={() => add("daily")}>روزمزد</Button>
          <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={() => add("hourly")}>ساعتی</Button>
          <Button size="small" icon={<PlusOutlined />} disabled={disabled} onClick={() => add("amount")}>مبلغ</Button>
        </Space>
      </div>
      {rows.length > 0 ? (
        <Table size="small" pagination={false} rowKey="_key" dataSource={rows} columns={columns}
          scroll={{ x: 900 }}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <Space split="•" wrap>
                  <span>افزایش: <b style={{ color: "#107C41" }}>{formatMoney(totals.earn)}</b></span>
                  <span>کسر: <b style={{ color: "#C0392B" }}>{formatMoney(totals.deduct)}</b></span>
                  <span>اثر روی خالص: <b style={{ color: totals.earn - totals.deduct < 0 ? "#C0392B" : "#107C41" }}>
                    {formatMoney(totals.earn - totals.deduct)}</b></span>
                </Space>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} colSpan={3} />
            </Table.Summary.Row>
          )} />
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>ردیفِ اصلاحی‌ای برای این نفر ثبت نشده.</Text>
      )}
    </div>
  );
}
